import React from 'react';
// @ts-ignore
import { Security } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl, AccessToken } from '@okta/okta-auth-js';
import { useHistory } from 'react-router-dom';
import type { RestoreOriginalUriFunction } from '@okta/okta-react/bundles/types/OktaContext';
import { datadogRum } from '@datadog/browser-rum';

type Props = {
  oktaAuth: OktaAuth;
  children: React.ReactNode;
};

export const SecureContainer = (props: Props) => {
  const history = useHistory();
  const restoreOriginalUri: RestoreOriginalUriFunction = async (
    _: OktaAuth,
    originalUri: string,
  ) => {
    history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  props.oktaAuth.tokenManager.on('expired', (key, token) => {
    if (key === 'accessToken') {
      // Okta triggers the expiry event 30s earlier than the actual token expiry.
      // We only want to restore pages that have been left idle in the browser.
      // If you had a single page open and left it for another domain while the tokens were active,
      // then okta-auth-js does not remove the accessToken from localStorage.
      // Instead, when you com back the 'expired' event triggers for the accessToken
      // and you land on the Expiration page instead of Sign-in.
      // There is no good option in okta-auth-js to differ between an idle tab and revisit.
      //
      // To ensure that only idle pages are redirected,
      // check that the actual expiry time is not yet passed.
      //
      // If a page is revisited before token expity, but after exp-30s,
      // the SecureRoute redirects to Sign-In.
      const timestamp = Math.floor(Date.now() / 1000);
      if (token.expiresAt >= timestamp) {
        const { origin } = window.location;
        const path = window.location.href.substring(origin.length);
        if (!path.startsWith('/expired')) {
          const originalUri = `/expired?original_path=${encodeURIComponent(path)}`;
          history.push(originalUri);
        }
      }
    }
  });

  props.oktaAuth.tokenManager.on('added', (key, token) => {
    if (key === 'accessToken') {
      const tk = token as AccessToken;
      const userRoles = (tk.claims.cko_user_roles as string[]) ?? [];
      const user = {
        id: tk.claims.cko_user_id as string,
        client_id: tk.claims.cko_client_id,
        user_type: tk.claims.cko_user_type,
        user_roles: userRoles.join(', '),
      };
      datadogRum.setUser(user);
    }
  });

  return (
    <Security oktaAuth={props.oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      {props.children}
    </Security>
  );
};
